import { type FloorBandBox } from './floor-band.js';

import { buildExplorationCellRegistry } from '../../../ft3d/engine/ft3d-exploration.js';
export { buildExplorationCellRegistry, explorationVisibilityCells, canonicalExplorationCellJson, canonicalExplorationNumber,
  sha256Hex, FT3D_EXPLORATION_CELL_SCHEMA, FT3D_EXPLORATION_REGISTRY_SCHEMA } from '../../../ft3d/engine/ft3d-exploration.js';

export interface Ft3dExplorationBox extends FloorBandBox {
  id: string;
}

export interface Ft3dExplorationCell {
  id: string;
  roomId: string;
  x: { min: number; max: number };
  y: { min: number; max: number };
  floor: number;
  ceiling: number;
  cellKey?: string;
}

export interface Ft3dExplorationShape {
  boxes: Ft3dExplorationBox[];
}

/** The held native planner owns membership; persistence compares only saved row identities/revisions. */
export function planExplorationCommand(
  registry: { geometryRevision: string; cellSizeFt: number; cells: Ft3dExplorationCell[] },
  rows: readonly { id: string; revision: number; cellKeys: readonly string[] }[], command: 'reset' | 'reconcile',
) {
  const valid = new Set(registry.cells.map(cell => cell.cellKey));
  return { schema: 'ft.ft3d.exploration-command-plan.v1', geometryRevision: registry.geometryRevision,
    cellSizeFt: registry.cellSizeFt, states: rows.map(row => ({ id: row.id, revision: row.revision,
      cellKeys: command === 'reset' ? [] : row.cellKeys.filter(key => valid.has(key)) })) };
}

/** The save endpoint accepts partial registries and retains prior discovery.
 * Send only this preview's discovered cells, in bounded packets, while retaining
 * the full geometry identity. Large outdoor scenes must not upload every unseen cell. */
export function explorationUploadBatches<R extends { cells: Ft3dExplorationCell[] }, P extends { visibleRoomCells: Ft3dExplorationCell[] }>(
  registry: R, preview: P,
): Array<{ registry: R; preview: P }> {
  const registered = new Map(registry.cells.map(cell => [String(cell.cellKey).toLowerCase(), cell]));
  const cells = [...new Map(preview.visibleRoomCells.map(cell => [String(cell.cellKey).toLowerCase(), cell])).values()];
  const batches: Array<{ registry: R; preview: P }> = [];
  for (let start = 0; start < cells.length; start += 1024) {
    const visible = cells.slice(start, start + 1024);
    const subset = visible.map(cell => {
      const held = registered.get(String(cell.cellKey).toLowerCase());
      if (!held) throw new Error('geometry:exploration-cell-not-registered');
      return held;
    });
    batches.push({ registry: { ...registry, cells: subset }, preview: { ...preview, visibleRoomCells: visible } });
  }
  return batches;
}

/** Bootstrap projections can precede the scene's perception cache. Reuse only a matching registry. */
export async function explorationRegistryForPreview(shape: Ft3dExplorationShape, revision: string,
  cached?: Awaited<ReturnType<typeof buildExplorationCellRegistry>> | null,
): Promise<Awaited<ReturnType<typeof buildExplorationCellRegistry>> | null> {
  const registry = cached?.geometryRevision === revision ? cached : await buildExplorationCellRegistry(shape);
  return registry.geometryRevision === revision ? registry : null;
}
